import React from 'react';
import framework from 'utils/framework';
import Events from 'utils/framework/Events';
const { wrapComponent } = framework;
import BaseClass from 'components/BaseClass';

import { Container } from 'components/ui';
import analyticsConsts from 'analytics/constants';
import BackToTopButton from 'components/BackToTopButton/BackToTopButton';
import localeUtils from 'utils/LanguageLocale';
import Actions from 'Actions';
import ProductActions, { isPdpFulfillmentEligibleSku } from 'actions/ProductActions';
import AddToBasketActions from 'actions/AddToBasketActions';
import ProductPageSEO from 'components/ProductPage/ProductPageSEO/ProductPageSEO';
import DigitalProduct from 'components/ProductPage/Type/DigitalProduct/DigitalProduct';
import SDUProductPage from 'components/ProductPage/Type/DigitalProduct/SDUProductPage';
import RegularProduct from 'components/ProductPage/Type/RegularProduct';
import RewardProduct from 'components/ProductPage/Type/RewardProduct/RewardProduct';
import SetPageAnalyticsProps from 'components/Analytics';
import productPageBindings from 'analytics/bindingMethods/pages/productPage/productPageBindings';
import dateUtils from 'utils/Date';
import skuUtils from 'utils/Sku';
import skuHelpers from 'utils/skuHelpers';
import store from 'store/Store';
import urlUtils from 'utils/Url';
import userUtils from 'utils/User';
import Location from 'utils/Location';
import { PostLoad, TestTargetReady } from 'constants/events';
import MobileStickyMenu from 'components/ProductPage/MobileStickyMenu/MobileStickyMenu';
import { breakpoints } from 'style/config';
import actions from 'actions/Actions';
import { Pages } from 'constants/Pages';
import basketConstants from 'constants/Basket';
import BasketActionsEN from 'actions/locales/BasketActions_en_US.js';
import BasketActionsFR from 'actions/locales/BasketActions_fr_CA.js';
import ProductPageEN from 'pages/Product/locales/ProductPage_en_US.js';
import ProductPageFR from 'pages/Product/locales/ProductPage_fr_CA.js';
import { fireRoktPageView, fireRoktViewDetail } from 'utils/adUtils';

const getBasketText = localeUtils.getLocalisedDataFromResource(BasketActionsEN, BasketActionsFR);
const getText = localeUtils.getLocalisedDataFromResource(ProductPageEN, ProductPageFR);
const { BasketType, DEEPLINK_ADD_TO_BASKET_SOURCES } = basketConstants;
const {
    DEEPLINK_US_SMS, DEEPLINK_CA_SMS, DEEPLINK_US_EMAIL, DEEPLINK_CA_EMAIL, REPLEN_US_SMS, REPLEN_CA_SMS
} = DEEPLINK_ADD_TO_BASKET_SOURCES;

const {
    PRODUCT_IDS: { SAMPLE },
    SKU_ID_PARAM,
    skuTypes: { REWARD, ROUGE_REWARD_CARD, STANDARD, SDU }
} = skuUtils;
const {
    SKU_UPDATE_SOURCE: { QUERY_STRING }
} = ProductActions;

const SKU_DATA_READY = 'skuDataReady';
const USER_SPECIFIC_DATA_READY = 'userSpecificDataReady';
const FULFILLMENT_DATA_READY = 'fulfillmentDataReady'; // GUAR-8295: PAGE_LOAD gate for fulfillmentOptions
// GUAR-8371: safety-net for the analytics gate. The initial getFulfillmentOptions
// dispatch is now issued in ProductActions.openPage (SPA nav) and in
// componentDidMount below (hard load), so the fetch has a much larger head-start
// than under the previous 500 ms budget — the timeout only trips when the fetch
// legitimately hangs or errors.
const FULFILLMENT_DATA_TIMEOUT_MS = 2000;

const ADD_TO_BASKET_PARAM = 'om_mmc';
const ADD_TO_BASKET_MAPPING = {
    [ADD_TO_BASKET_PARAM]: [DEEPLINK_US_SMS, DEEPLINK_CA_SMS, DEEPLINK_US_EMAIL, DEEPLINK_CA_EMAIL, REPLEN_US_SMS, REPLEN_CA_SMS]
};
const ADD_TO_BASKET_DEFAULT_QTY = 1;

class ProductPage extends BaseClass {
    constructor(props) {
        super(props);
        const {
            page: { product }
        } = store.getState();

        this.state = {
            product,
            showStickyMenu: false,
            alreadyTriedToAddProductToBasket: false,
            stickyNavBarPdpActive: false
        };
    }

    shouldSendAnalytics = {
        [SKU_DATA_READY]: false,
        [USER_SPECIFIC_DATA_READY]: false,
        [FULFILLMENT_DATA_READY]: false // GUAR-8295: gates PAGE_LOAD until fulfillmentOptions or OOS or timeout
    };

    fulfillmentDataTimeout = null;

    // GUAR-8124: Track if this is initial page load (true) or SPA navigation (false)
    isInitialPageLoad = true;

    // GUAR-8371: Restart the safety-net timeout that flips FULFILLMENT_DATA_READY
    // if the fulfillment fetch never lands. Not started when no fetch is expected
    // (see willFetchFulfillmentOptions) — the gate is released synchronously in
    // that case.
    startFulfillmentDataTimeout = () => {
        clearTimeout(this.fulfillmentDataTimeout);
        this.fulfillmentDataTimeout = setTimeout(() => {
            if (!this.shouldSendAnalytics[FULFILLMENT_DATA_READY]) {
                this.initializeAnalyticsIfAllDataReady(FULFILLMENT_DATA_READY);
            }
        }, FULFILLMENT_DATA_TIMEOUT_MS);
    };

    // GUAR-8371: Returns true only when ProductActions.getFulfillmentOptions
    // will actually fire an API call for this product, so the analytics gate
    // does not wait for a response that will never come.
    //
    // The eligibility is enforced across two layers:
    //   1. The thunk itself only bails when the config flag is off, no
    //      preferredZipCode is set, or currentSku.skuId is missing.
    //   2. The OOS / non-STANDARD SKU short-circuits live at the call sites
    //      (openPage, componentDidMount, useFulfillment) via the shared
    //      isPdpFulfillmentEligibleSku helper — the thunk itself does NOT
    //      check OOS or product type.
    // This method must therefore combine BOTH layers to know whether a fetch
    // will actually happen.
    willFetchFulfillmentOptions = product => {
        if (product?.fulfillmentOptions) {
            return false;
        }

        if (!isPdpFulfillmentEligibleSku(product?.currentSku)) {
            return false;
        }

        if (!Sephora.configurationSettings?.shipToHomeEligibleInPDP) {
            return false;
        }

        // GUAR-8371: Must match ProductActions.getFulfillmentOptions, which
        // reads user.preferredZipCode only. userUtils.getZipCode() would fall
        // back to the selected-store postal code and cause the gate to wait
        // for a request the thunk will not issue.
        if (!store.getState().user?.preferredZipCode) {
            return false;
        }

        return true;
    };

    // GUAR-8295 / GUAR-8371: Flip FULFILLMENT_DATA_READY when fulfillmentOptions
    // has arrived, the SKU is OOS, or no fulfillment fetch will happen for this
    // product. No-op if flag is already set.
    checkFulfillmentDataReady = product => {
        if (this.shouldSendAnalytics[FULFILLMENT_DATA_READY]) {
            return;
        }

        if (!this.willFetchFulfillmentOptions(product)) {
            clearTimeout(this.fulfillmentDataTimeout);
            this.initializeAnalyticsIfAllDataReady(FULFILLMENT_DATA_READY);
        }
    };

    // GUAR-8295: Sets eddDate in digitalData when fulfillmentOptions are available.
    // Does NOT fire a separate analytics call — event320/eVar141 are included in the
    // existing PAGE_LOAD via getPageEvents() when data is ready in the Redux store.
    setEddInDigitalData = product => {
        const deliveryDate = product?.fulfillmentOptions?.deliveryOptions?.shipToHome?.promiseDate?.[0]?.deliveryDate;
        const isOOS = product?.currentSku?.isOutOfStock;

        if (!isOOS && deliveryDate) {
            const eddData = dateUtils.getEddAnalyticsData(deliveryDate);

            if (eddData) {
                digitalData.page.attributes.eddDate = eddData.eddDate;
            }
        }
    };

    componentDidMount() {
        delete digitalData.page.attributes.eddDate;

        store.setAndWatch('page.product', this, null, true);
        store.setAndWatch('page.product.isUserSpecificReady', this, userSpecificFlag => {
            if (userSpecificFlag?.isUserSpecificReady) {
                this.initializeAnalyticsIfAllDataReady(USER_SPECIFIC_DATA_READY);
            }
        });

        // GUAR-8295: Listen for fulfillmentOptions arriving in the product store
        // to release the FULFILLMENT_DATA_READY gate so PAGE_LOAD can fire with EDD.
        store.setAndWatch('page.product.fulfillmentOptions', this, fulfillmentSlice => {
            if (fulfillmentSlice?.fulfillmentOptions) {
                this.checkFulfillmentDataReady(store.getState().page.product);
            }
        });

        // GUAR-8371: Only kick off the fetch when it would actually be issued
        // for this product (STANDARD sku, in-stock, config on, zip present, not
        // already in store). Avoids unnecessary SDN calls for Reward/Digital/SDU
        // and OOS SKUs on hard load. Release the analytics gate synchronously
        // in the same non-eligible cases so PAGE_LOAD fires without stalling.
        // The non-eligible branch also cancels any previously-armed safety-net
        // timer (defensive: fresh mount shouldn't have one, but this guarantees
        // no stale timer can later flip FULFILLMENT_DATA_READY out of turn).
        if (this.willFetchFulfillmentOptions(this.state.product)) {
            store.dispatch(ProductActions.getFulfillmentOptions());
            this.startFulfillmentDataTimeout();
        } else {
            clearTimeout(this.fulfillmentDataTimeout);
            this.initializeAnalyticsIfAllDataReady(FULFILLMENT_DATA_READY);
        }

        // Wait for the initial personalized product gets into the store
        Events.onLastLoadEvent(window, [Events.ProductInfoReady], () => {
            userUtils.validateUserStatus().then(() => {
                store.setAndWatch('user', this, (newData, oldData) => {
                    const userIsUpdatedForTheFirstTime = newData.user && !oldData;
                    const userProfileHasChanged = newData.user.profileId !== oldData?.user.profileId;

                    if (!userIsUpdatedForTheFirstTime && userProfileHasChanged) {
                        store.dispatch(ProductActions.updateProductWithUserSpecificData());
                    }

                    // Make sure we call `addToBasketFromURLParameter` method just once,
                    // regardless of new user data
                    if (this.state.alreadyTriedToAddProductToBasket === false) {
                        this.setState({
                            alreadyTriedToAddProductToBasket: true
                        });

                        // This is the spot where it's safe to call this method, giving that
                        // for basket inclusion, we need to have user and product data at hand.
                        this.addToBasketFromURLParameter();
                    }
                });
            });
        });

        const productName = this.state.product?.productDetails?.displayName || this.state.product?.currentSku?.productName || '';
        fireRoktPageView({ pagetype: 'product', screenname: productName, title: productName });
        fireRoktViewDetail(this.state.product);

        const showStickyMenu = Sephora.isMobile() && window.matchMedia(breakpoints.xsMax).matches;
        this.setState({ showStickyMenu });

        // Hide legacy BackToTopButton while the Sticky Nav PDP A/B test is active — the sticky bar owns the top-anchor jump link.
        Sephora.Util.onLastLoadEvent(window, [TestTargetReady], () => {
            store.setAndWatch('testTarget', this, newOffers => {
                const experience = newOffers.testTarget?.offers?.stickyNavBarPdp?.experience;
                const stickyNavBarPdpActive = experience === 'challengerOne' || experience === 'challengerTwo';

                if (this.state.stickyNavBarPdpActive !== stickyNavBarPdpActive) {
                    this.setState({ stickyNavBarPdpActive });
                }
            });
        });

        Sephora.Util.onLastLoadEvent(window, [PostLoad], () => {
            const product = this.state.product;

            if (!product?.currentSku?.isOutOfStock) {
                this.addItemsFromTsl();
            }

            // GUAR-8295: Set eddDate in digitalData for late-arriving fulfillmentOptions.
            // event320/eVar141 are already included in the PAGE_LOAD via getPageEvents().
            if (this.isInitialPageLoad && !digitalData.page.attributes.eddDate) {
                this.setEddInDigitalData(product);
            }
        });
    }

    componentDidUpdate(_prevProps, prevState) {
        const productIdChanged = prevState.product?.productDetails?.productId !== this.state.product?.productDetails?.productId;

        if (productIdChanged) {
            const productName = this.state.product?.productDetails?.displayName || this.state.product?.currentSku?.productName || '';
            fireRoktPageView({ pagetype: 'product', screenname: productName, title: productName });
            fireRoktViewDetail(this.state.product);

            this.shouldSendAnalytics[USER_SPECIFIC_DATA_READY] = false;

            // GUAR-8124: Mark that we're now in SPA navigation mode so the PostLoad handler
            // in componentDidMount doesn't re-fire setEddInDigitalData for the previous product.
            this.isInitialPageLoad = false;

            // Clear stale eddDate when navigating to a different product
            delete digitalData.page.attributes.eddDate;

            // GUAR-8295 / GUAR-8371: Re-arm the fulfillment gate for the new product.
            // ProductActions.openPage already dispatched getFulfillmentOptions for the
            // new product; we only start the safety-net timeout if a fetch will actually
            // occur (see willFetchFulfillmentOptions), otherwise release synchronously.
            // Either way, cancel any safety-net timer left over from the previous
            // product so it can't fire against the new one's gate. startFulfillmentDataTimeout
            // clears the same handle before arming, so the eligible branch is unaffected.
            this.shouldSendAnalytics[FULFILLMENT_DATA_READY] = false;

            if (this.willFetchFulfillmentOptions(this.state.product)) {
                this.startFulfillmentDataTimeout();
            } else {
                clearTimeout(this.fulfillmentDataTimeout);
                this.initializeAnalyticsIfAllDataReady(FULFILLMENT_DATA_READY);
            }

            // GUAR-8295: Set eddDate for SPA navigation; event320 is included via getPageEvents()
            this.setEddInDigitalData(this.state.product);
        }

        // Swatch Changes require to update the digitalData.product value. Otherwise it is empty on every change.
        // GUAR-8295: Guard with !productIdChanged so SPA navigation doesn't bypass the
        // FULFILLMENT_DATA_READY gate. True swatch changes (same product, different SKU)
        // still update digitalData.product immediately.
        if (!productIdChanged && prevState.product?.currentSku?.displayName !== this.state.product?.currentSku?.displayName) {
            productPageBindings.initializeAnalyticsObjectWithProductData();
        }

        // GUAR-8124: Handle late-arriving fulfillmentOptions
        // This fires when fulfillmentOptions loads after the product has already been set
        const fulfillmentOptionsJustArrived = !prevState.product?.fulfillmentOptions && !!this.state.product?.fulfillmentOptions;
        const eddNotYetCaptured = !digitalData.page.attributes.eddDate;

        if (fulfillmentOptionsJustArrived && eddNotYetCaptured) {
            this.setEddInDigitalData(this.state.product);
        }

        // GUAR-8295: Release fulfillment gate when fulfillmentOptions arrives or OOS is detected
        if (fulfillmentOptionsJustArrived) {
            this.checkFulfillmentDataReady(this.state.product);
        }

        const oosJustDetected = !prevState.product?.currentSku?.isOutOfStock && !!this.state.product?.currentSku?.isOutOfStock;

        if (oosJustDetected) {
            this.checkFulfillmentDataReady(this.state.product);
        }
    }

    componentWillUnmount() {
        // GUAR-8124: On un-mount remove eddDate from next calls
        digitalData.page.attributes.eddDate && delete digitalData.page.attributes.eddDate;

        // GUAR-8295: clear the fulfillment-data safety-net timeout
        clearTimeout(this.fulfillmentDataTimeout);
    }

    shouldComponentUpdate(_nextProps, nextState) {
        const {
            product: { currentSku, errorCode, productDetails }
        } = this.state;
        const currentSkuType = skuUtils.getProductType(currentSku);
        const nextSkuType = skuUtils.getProductType(nextState.product.currentSku);

        return (
            nextState.product.errorCode !== errorCode ||
            nextSkuType !== currentSkuType ||
            nextState.product.productDetails?.productId !== productDetails?.productId
        );
    }

    addItemsFromTsl = () => {
        const currentUrl = window.location.href;
        const { product } = this.state;
        const currentSku = product.currentSku;

        if (currentUrl.includes('c3ch=Linkshare') && currentUrl.includes(`c3nid=${Pages.tslId}`)) {
            try {
                store.dispatch(
                    AddToBasketActions.addToBasket(currentSku, BasketType.Standard, ADD_TO_BASKET_DEFAULT_QTY, () => {
                        if (window.matchMedia(breakpoints.smMin).matches) {
                            store.dispatch(
                                actions.showAddToBasketModal({
                                    product: product,
                                    sku: currentSku,
                                    quantity: ADD_TO_BASKET_DEFAULT_QTY,
                                    isOpen: true
                                })
                            );
                        }
                    })
                );
            } catch {
                store.dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        message: getBasketText('genericErrorTitle'),
                        buttonText: getBasketText('gotIt')
                    })
                );
            }
        }
    };

    // Extracted helper to determine pageType and content for a given SKU
    determinePageTypeAndContent = currentSku => {
        const currentSkuType = skuUtils.getProductType(currentSku);
        let pageType = analyticsConsts.PAGE_TYPES.PRODUCT;
        let content;

        switch (currentSkuType) {
            case ROUGE_REWARD_CARD: {
                const { rewardPoints, rewardValue } = currentSku;
                let subtitle = '';

                if (rewardPoints && rewardValue) {
                    subtitle = getText('subtitle', [rewardPoints, rewardValue]);
                }

                pageType = analyticsConsts.PAGE_TYPES.REWARDS;
                content = (
                    <DigitalProduct
                        subtitle={subtitle}
                        processSkuId={this.processSkuId}
                    />
                );

                break;
            }
            case REWARD: {
                pageType = analyticsConsts.PAGE_TYPES.REWARDS;
                content = <RewardProduct processSkuId={this.processSkuId} />;

                break;
            }
            case SDU: {
                content = (
                    <SDUProductPage
                        skipConfirmationModal={true}
                        processSkuId={this.processSkuId}
                    />
                );

                break;
            }
            case STANDARD:
            default: {
                content = <RegularProduct processSkuId={this.processSkuId} />;

                break;
            }
        }

        return { pageType, content };
    };

    // Extracted helper to detect face out variation and resolve variation id
    getVariationInfo = (product, currentSku) => {
        const hasFaceoutVariation =
            (Array.isArray(product?.skus) && product.skus.length > 1) ||
            !!(currentSku?.variationValue || currentSku?.variation_type || currentSku?.variation_id || currentSku?.color || currentSku?.size);

        const variationId =
            currentSku?.variation_id ||
            currentSku?.data?.variation_id ||
            currentSku?.variationId ||
            (hasFaceoutVariation ? currentSku?.skuId : undefined);

        return { hasFaceoutVariation, variationId };
    };

    render() {
        // This should be broken down and accessed from subcomponents that need specific
        // data rather than being pass through the high level page components
        const {
            product,
            product: { currentSku }
        } = this.state;

        // Determine page type and content via helper to reduce render complexity
        const { pageType, content } = this.determinePageTypeAndContent(currentSku);
        const showStickyMenu = skuUtils.getProductType(currentSku) === STANDARD && this.state.showStickyMenu;

        // Variation info (face-out + variation id) moved to helper
        const { hasFaceoutVariation, variationId } = this.getVariationInfo(product, currentSku);

        // Resolve product display name for Constructor attribute
        const productDisplayName = product?.productDetails?.displayName || currentSku?.productName || product?.productName || undefined;

        // Simple item price: prefer salePrice, fallback to listPrice (omit attribute if neither)
        const productPrice = currentSku?.salePrice || currentSku?.listPrice || undefined;

        return (
            <Container
                is='main'
                data-at={Sephora.debug.dataAt('pdp_page_container')}
                data-cnstrc-product-detail
                data-cnstrc-item-id={product?.productDetails?.productId || undefined}
                data-cnstrc-item-name={productDisplayName}
                data-cnstrc-item-price={productPrice}
                {...(hasFaceoutVariation && variationId ? { 'data-cnstrc-item-variation-id': variationId } : {})}
            >
                {showStickyMenu && <MobileStickyMenu />}
                <SetPageAnalyticsProps
                    pageType={pageType}
                    pageName={product?.productDetails?.productId}
                />
                {content}
                {!showStickyMenu && !this.state.stickyNavBarPdpActive && <BackToTopButton analyticsLinkName={analyticsConsts.LinkData.BACK_TO_TOP} />}
                <ProductPageSEO product={product} />
            </Container>
        );
    }

    initializeAnalyticsIfAllDataReady = keyThatsReady => {
        if (Object.values(this.shouldSendAnalytics).every(x => x)) {
            return; // already sent
        }

        this.shouldSendAnalytics[keyThatsReady] = true;

        if (Object.values(this.shouldSendAnalytics).every(x => x)) {
            productPageBindings.initializeAnalyticsObjectWithProductData();
        }
    };

    processSkuId = component => {
        const {
            page: {
                product,
                product: { productDetails }
            }
        } = store.getState();
        let skuId = urlUtils.getParamsByName(SKU_ID_PARAM);
        component.isSkuReady = true;
        const dispatchParams = { disableDispatchWarning: true };

        if (Array.isArray(skuId) && skuId.length > 0) {
            skuId = skuId[0];

            // TODO: Remove sample specific code if we kill sample ppages.  Otherwise, find more elegant
            // solution (separate component for samples?
            // If we have a sample, we have to get the full product data
            // Note: we check by sample product id here because we don't have the full sku data to
            // check skuType
            if ((productDetails || product).productId === SAMPLE) {
                store.dispatch(ProductActions.fetchCurrentProduct(SAMPLE, skuId), dispatchParams);
            } else {
                const skuFromProduct = skuHelpers.getSkuFromProduct(product, skuId);

                // Confirm that this sku is present in the product
                if (skuFromProduct) {
                    store.dispatch(ProductActions.updateSkuInCurrentProduct(skuFromProduct, QUERY_STRING), dispatchParams);
                }
            }
        }

        // this call should be made at the end of this method, so at this point all SKU data will be updated and ready to use
        this.initializeAnalyticsIfAllDataReady(SKU_DATA_READY);
    };

    addToBasketSuccess = () => {
        Location.navigateTo(null, '/basket');
    };

    addToBasketFailure = error => {
        store.dispatch(Actions.showInterstice(false));

        if (error.errorMessages) {
            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    message: error.errorMessages[0],
                    buttonText: getBasketText('gotIt')
                })
            );
        }
    };

    addToBasketFromURLParameter = async () => {
        const addToBasketURLQuery = urlUtils.getParamsByName(ADD_TO_BASKET_PARAM);
        const addToBasketURLParam = addToBasketURLQuery ? addToBasketURLQuery[0] : null;

        // If no valid URL param, skip functionality
        if (!addToBasketURLParam) {
            return;
        }

        const { product } = this.state;
        const { basket } = store.getState();
        const currentSku = product.currentSku;

        // Before proceeding, we should see if URL param is appropriate
        if (
            ADD_TO_BASKET_MAPPING[ADD_TO_BASKET_PARAM] &&
            ADD_TO_BASKET_MAPPING[ADD_TO_BASKET_PARAM].find(mappedParam => mappedParam === addToBasketURLParam)
        ) {
            // Make sure product is not already in basket
            if (Array.isArray(basket.items) && basket.items.find(basketItem => basketItem.sku.skuId === currentSku.skuId)) {
                store.dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        message: getBasketText('alreadyInCart'),
                        buttonText: getBasketText('gotIt')
                    })
                );

                return;
            }

            try {
                store.dispatch(Actions.showInterstice(true));

                await store.dispatch(AddToBasketActions.addToBasket(currentSku, false, ADD_TO_BASKET_DEFAULT_QTY, this.addToBasketSuccess));
            } catch (e) {
                this.addToBasketFailure(e);
            }
        }
    };
}

export default wrapComponent(ProductPage, 'ProductPage', true);
